import { VASTClient } from '../lib/VASTClient';
import { fetchVmapUrl } from '../lib/VMAPParser';
import { convertTimeOffsetToSeconds } from '../lib/utils';
import SimpleVast from '../index';

// The preroll breaks are fetched in parallel; once the last of them has settled, play whatever
// they returned. The count lives on the instance: a plugin disposed while its requests are out
// (an episode switch) must not throw off the count of the one that replaced it.
function settlePreroll() {
  this._pendingPrerolls -= 1;
  if (this._pendingPrerolls > 0 || this._disposed) return;
  if (this.adsArray.length > 0) {
    this.readAd();
  } else {
    this._resumeContent();
  }
}

export function parseInlineVastData(vastAdData, adType) {
  const xmlString = new XMLSerializer().serializeToString(vastAdData);
  const vastXml = new window.DOMParser().parseFromString(xmlString, 'text/xml');
  const client = new VASTClient();

  client.parseVAST(vastXml)
    .then((parsed) => {
      const ads = parsed.ads ?? [];

      if (adType === 'postroll') {
        this.postRollData = ads;
      } else if (adType === 'preroll') {
        this.adsArray.push(...ads);
        settlePreroll.call(this);
      } else if (adType === 'midroll') {
        this.adsArray = ads;
        this.readAd(); // an empty break releases the mid-roll lock
      }
    })
    .catch((err) => {
      this._captureError('SimpleVast: Error parsing inline VAST data', { error: err, adType });
      if (adType === 'preroll') settlePreroll.call(this);
      if (adType === 'midroll') this.readAd();
    });
}

export async function handleVMAP(vmapUrl) {
  try {
    const vmap = await fetchVmapUrl(vmapUrl);
    if (this._disposed) return;

    if (!vmap.adBreaks || vmap.adBreaks.length === 0) {
      this._resumeContent();
      return;
    }

    this.addEventsListeners();

    // ── Preroll ──────────────────────────────────────────────────────────────
    const prerolls = SimpleVast.getPreroll(vmap.adBreaks);
    this._pendingPrerolls = prerolls.length;

    if (!prerolls.length) {
      this._resumeContent();
    } else {
      prerolls.forEach(async (preroll) => {
        if (preroll.adSource?.adTagURI?.uri) {
          await this.handleVAST(preroll.adSource.adTagURI.uri);
          settlePreroll.call(this);
        } else if (preroll.adSource?.vastAdData) {
          this.parseInlineVastData(preroll.adSource.vastAdData, 'preroll');
        } else {
          settlePreroll.call(this);
        }
      });
    }

    // ── Postroll ─────────────────────────────────────────────────────────────
    const postroll = SimpleVast.getPostroll(vmap.adBreaks);
    if (postroll?.adSource?.adTagURI?.uri) {
      this.postRollUrl = postroll.adSource.adTagURI.uri;
    } else if (postroll?.adSource?.vastAdData) {
      this.parseInlineVastData(postroll.adSource.vastAdData, 'postroll');
    }

    // ── Midrolls ─────────────────────────────────────────────────────────────
    this.watchForProgress = SimpleVast.getMidrolls(vmap.adBreaks);
    if (this.watchForProgress.length > 0) {
      this.video.addEventListener('timeupdate', this.onProgress);
      this.video.addEventListener('seeking', this._onContentSeeking);
    }
  } catch (err) {
    if (this._disposed) return;
    this._captureError('SimpleVast: Error fetching/parsing VMAP', { error: err, tag: vmapUrl });
    this._resumeContent();
  }
}
